import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { post } from '../api';
import { askConfirm } from '../components/ask';
import type { InviteCompose } from '../components/ComposeButtons';
import { copyText } from '../components/CopyLink';
import { toast } from '../components/toast';
import { currentLocale } from '../dates';
import { describe } from '../errors';

export type Access = 'invited' | 'expired' | 'active' | 'off';

export interface Person {
  id: number;
  name: string;
  email: string;
  access: Access;
  invited_at: string | null;
  last_login_at: string | null;
}

export const day = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString(currentLocale(), {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : 'Never';

/** "sent today", "sent yesterday", "sent 3 days ago". */
export function sentAgo(iso: string, now = Date.now()) {
  const days = Math.max(0, Math.floor((now - new Date(iso).getTime()) / 86_400_000));
  return days === 0 ? 'sent today' : days === 1 ? 'sent yesterday' : `sent ${days} days ago`;
}

const PILL: Record<Access, { cls: string; text: string }> = {
  invited: { cls: 'pill info', text: 'Invited' },
  expired: { cls: 'pill warn', text: 'Invite expired' },
  active: { cls: 'pill ok', text: 'Active' },
  off: { cls: 'pill', text: 'Switched off' },
};

export function AccessPill(props: { access: Access; invitedAt: string | null }) {
  const p = PILL[props.access];
  return (
    <>
      <span className={p.cls}>{p.text}</span>
      {props.access === 'invited' && props.invitedAt && (
        <span className="muted"> {sentAgo(props.invitedAt)}</span>
      )}
    </>
  );
}

interface MenuItem {
  label: string;
  /** Accessible name: the visible label plus who it is for. */
  name: string;
  onSelect(): void;
}

/** A button that opens a small menu. Escape and a click elsewhere close it and put focus back on the button. */
export function MoreMenu(props: { label: string; disabled?: boolean; items: MenuItem[] }) {
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const id = useId();

  useEffect(() => {
    if (!open) return;
    menu.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    const away = (e: MouseEvent) => {
      if (!menu.current?.contains(e.target as Node) && e.target !== trigger.current) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  if (props.items.length === 0) return null;
  const close = () => {
    setOpen(false);
    trigger.current?.focus();
  };
  const move = (dir: 1 | -1) => {
    const all = [...(menu.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])];
    const i = all.indexOf(document.activeElement as HTMLElement);
    all[(i + dir + all.length) % all.length]?.focus();
  };
  return (
    <span className="more">
      <button
        ref={trigger}
        type="button"
        className="quiet"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        aria-label={props.label}
        disabled={props.disabled}
        onClick={() => setOpen((o) => !o)}
      >
        More
      </button>
      {open && (
        <div
          ref={menu}
          id={id}
          role="menu"
          className="more-menu"
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              close();
            }
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              move(1);
            }
            if (e.key === 'ArrowUp') {
              e.preventDefault();
              move(-1);
            }
          }}
        >
          {props.items.map((it) => (
            <button
              key={it.name}
              type="button"
              role="menuitem"
              aria-label={it.name}
              className="quiet"
              onClick={() => {
                close();
                it.onSelect();
              }}
            >
              {it.label}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

export interface LinkShown {
  id: number;
  url: string;
  purpose: 'invite' | 'reset';
  /** The message ready to send from this device, when it is set to go by hand or could not go. */
  compose?: InviteCompose;
}
interface Sent {
  emailed?: boolean;
  link?: string;
  compose?: InviteCompose;
}

/** What the rows can do. `base` is `/api/portal-users` or `/api/staff-users`; `reload` refetches the list. */
export function useAccessActions(base: string, reload: () => void, onRun?: () => void) {
  const [pending, setPending] = useState<number | null>(null);
  const [problems, setProblems] = useState<string[]>([]);
  const [link, setLink] = useState<LinkShown | null>(null);
  // A ref as well as state: two clicks in the same tick must still run only one action.
  const busy = useRef(false);

  async function run(p: Person, work: () => Promise<void>) {
    if (busy.current) return; // a second click while one is running does nothing
    busy.current = true;
    setPending(p.id);
    setProblems([]);
    setLink(null);
    onRun?.(); // the invite form's own link and problems go too, so only one box is ever on screen
    try {
      await work();
      reload();
    } catch (err) {
      setProblems(describe(err).problems);
    } finally {
      busy.current = false;
      setPending(null);
    }
  }

  const mail = (p: Person, path: 'resend' | 'reset', purpose: 'invite' | 'reset', done: string) =>
    run(p, async () => {
      const r = await post<Sent>(`${base}/${p.id}/${path}`);
      if (r.emailed === false) {
        toast(`The email to ${p.name} was not sent. Give them the link below.`);
        if (r.link) setLink({ id: p.id, url: r.link, purpose, compose: r.compose });
      } else {
        toast(done);
        // Sent, but also set to go by hand on another channel (an SMS from this phone).
        if (r.compose) setLink({ id: p.id, url: '', purpose, compose: r.compose });
      }
    });

  return {
    pending,
    problems,
    setProblems,
    link,
    setLink,
    resend: (p: Person) => mail(p, 'resend', 'invite', `Invitation resent to ${p.email}.`),
    reset: (p: Person) => mail(p, 'reset', 'reset', `Password reset sent to ${p.email}.`),
    copyLink: (p: Person) =>
      run(p, async () => {
        const r = await post<{ link: string }>(`${base}/${p.id}/link`);
        setLink({ id: p.id, url: r.link, purpose: p.access === 'active' ? 'reset' : 'invite' });
        toast(
          (await copyText(r.link))
            ? `Link for ${p.name} copied. It works once.`
            : `Link for ${p.name} is below. Copy it from the box.`,
        );
      }),
    setActive: async (p: Person, active: boolean) => {
      if (!active) {
        const ok = await askConfirm({
          title: `Switch off ${p.name}?`,
          body: 'They will be signed out and will not be able to sign in until you switch them back on.',
          confirmLabel: 'Switch off',
          danger: true,
        });
        if (!ok) return;
      }
      await run(p, async () => {
        await post(`${base}/${p.id}/active`, { active });
        toast(active ? `${p.name} switched on.` : `${p.name} switched off.`);
      });
    },
  };
}

export type Actions = ReturnType<typeof useAccessActions>;

/** One primary button that fits the person's state, and the rest behind More. */
export function RowActions(props: {
  person: Person;
  actions: Actions;
  /** Their own row: nothing to do to yourself. */
  isSelf?: boolean;
  /** Portal tab only: opens the portal as this person. */
  onView?: () => void;
  canSwitchOff: boolean;
}) {
  const { person: p, actions: a } = props;
  if (props.isSelf) return null;
  const busy = a.pending === p.id;
  const btn = (label: string, name: string, onClick: () => void, cls = 'quiet') => (
    <button type="button" className={cls} aria-label={name} disabled={busy} onClick={onClick}>
      {label}
    </button>
  );
  const view = props.onView && {
    label: 'View portal',
    name: `View portal as ${p.name}`,
    onSelect: props.onView,
  };
  const menu: MenuItem[] = [];
  let primary: ReactNode = null;

  if (p.access === 'invited' || p.access === 'expired') {
    primary = btn(
      'Resend invite',
      `Resend invite to ${p.name}`,
      () => void a.resend(p),
      p.access === 'expired' ? '' : 'quiet',
    );
    menu.push({
      label: 'Copy link',
      name: `Copy link for ${p.name}`,
      onSelect: () => void a.copyLink(p),
    });
    if (view) menu.push(view);
  } else if (p.access === 'active') {
    if (view) primary = btn(view.label, view.name, view.onSelect);
    menu.push({
      label: 'Copy link',
      name: `Copy link for ${p.name}`,
      onSelect: () => void a.copyLink(p),
    });
    menu.push({
      label: 'Send password reset',
      name: `Send password reset to ${p.name}`,
      onSelect: () => void a.reset(p),
    });
  } else {
    primary = btn('Switch on', `Switch on ${p.name}`, () => void a.setActive(p, true));
  }
  if (p.access !== 'off' && props.canSwitchOff) {
    menu.push({
      label: 'Switch off',
      name: `Switch off ${p.name}`,
      onSelect: () => void a.setActive(p, false),
    });
  }

  return (
    <div className="actions-inner">
      {primary}
      <MoreMenu label={`More actions for ${p.name}`} disabled={busy} items={menu} />
    </div>
  );
}
